/*  MUI  */
import { Alert, AlertColor, Grid, Typography } from '@mui/material';

/*  Shared  */
import { Helpers, Route } from 'application/Shared/Helpers';
import { TenantApp } from 'application/Shared/types/tenantApp';

/*  NPM  */
import { differenceInCalendarDays, startOfDay } from 'date-fns';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

/**
 * Component that displays a contract alert for an application
 * Shows different severity levels based on days until contract renewal
 * @param tenantApp - Tenant application object containing contract details
 * @returns Alert component or null if no contract exists
 */
export const AppDetailsContractAlert = ({
    tenantApp
}: {
    tenantApp: TenantApp;
}) => {
    const { t } = useTranslation();
    const { applicationId, clientId, partnerId } = useParams();
    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    const isThereAContract = Helpers.isset(tenantApp, 'contracts', 0);

    if (!isThereAContract) {
        return null;
    }

    let contractAlert;
    let contractDays = 0;
    let contractSeverity = 'error' as AlertColor;
    const { contracts } = tenantApp;

    contracts?.forEach((contract) => {
        if (contract.type === 'renew') {
            const days = differenceInCalendarDays(
                startOfDay(new Date(contract.renewalDate)),
                startOfDay(new Date())
            );
            if (days <= 90) {
                contractAlert = true;
                contractDays = days;
                contractSeverity = 'info';
            }
            if (days <= 30) {
                contractSeverity = 'warning';
            }
            if (days <= 14) {
                contractSeverity = 'error';
            }
        }
    });

    const text =
        contractDays < 0
            ? 'Application.contractAlertPreTextExpired'
            : 'Application.contractAlertPreText';

    const dayCount = Math.abs(contractDays);

    return contractAlert ? (
        <Grid item xs={12}>
            <Alert severity={contractSeverity} icon={false}>
                <Typography
                    component="span"
                    dangerouslySetInnerHTML={{
                        __html: t(text, {
                            count: dayCount
                        })
                    }}
                />
                &nbsp;
                <Link
                    to={`${baseAdminRoute}/application/${applicationId}/contract`}
                    data-trackid="admin_appDetailsContractAlert_contract_routeTo"
                >
                    <Typography
                        variant="span"
                        sx={{
                            '&:hover': {
                                borderBottom: '1px solid',
                                borderColor: 'border.tertiary',
                                color: 'hover.tertiary'
                            },
                            color: `alert.text.${contractSeverity}`,
                            cursor: 'pointer',
                            fontWeight: 600
                        }}
                        aria-hidden="true"
                    >
                        {t('Application.contract_alert_view')} &raquo;
                    </Typography>
                </Link>
            </Alert>
        </Grid>
    ) : null;
};
